import { PageLoading } from "@bednbody/ui/components/page-loading";
import { Toaster } from "@bednbody/ui/components/sonner";
import { ThemeProvider } from "@bednbody/ui/lib/theme";
import { QueryClientProvider } from "@tanstack/react-query";
import { lazy, Suspense } from "react";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import { AppLayout } from "@/components/AppLayout";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { AnalyticsPage } from "@/features/analytics/AnalyticsPage";
import { AttendancePage } from "@/features/attendance/AttendancePage";
import { AuditPage } from "@/features/audit/AuditPage";
import { BookingsPage } from "@/features/bookings/BookingsPage";
import { CalendarRoute } from "@/features/calendar/CalendarRoute";
import { CashRemittancePage } from "@/features/cash/CashRemittancePage";
import { CatalogPage } from "@/features/catalog/CatalogPage";
import { DispatchPage } from "@/features/dispatch/DispatchPage";
import { DocumentsPage } from "@/features/documents/DocumentsPage";
import { GiftPassPage } from "@/features/giftpass/GiftPassPage";
import { GiftpassOrdersPage } from "@/features/giftpass-orders/GiftpassOrdersPage";
import { PayoutsPage } from "@/features/payouts/PayoutsPage";
import { ExecLogsPage } from "@/features/exec-logs/ExecLogsPage";
import { RestrictionsPage } from "@/features/restrictions/RestrictionsPage";
import { HubsPage } from "@/features/hubs/HubsPage";
import { KycReviewPage } from "@/features/kyc/KycReviewPage";
import { LegalPage } from "@/features/legal/LegalPage";
import { MileagePage } from "@/features/mileage/MileagePage";
import { OpsDashboardPage } from "@/features/ops-dashboard/OpsDashboardPage";
import { PayrollPage } from "@/features/payroll/PayrollPage";
import { QualificationsPage } from "@/features/qualifications/QualificationsPage";
import { RiderSessionsPage } from "@/features/rider-sessions/RiderSessionsPage";
import { RankingsPage } from "@/features/rankings/RankingsPage";
import { RosterPage } from "@/features/roster/RosterPage";
import { SchedulesPage } from "@/features/schedules/SchedulesPage";
import { SettingsPage } from "@/features/settings/SettingsPage";
import { AppStatusPage } from "@/features/app-status/AppStatusPage";
import { SupportPage } from "@/features/support/SupportPage";
import { TherapistSessionsPage } from "@/features/therapist-sessions/TherapistSessionsPage";
import { AuthProvider } from "@/lib/auth";
import { rolesFor } from "@/lib/permissions";
import { queryClient } from "@/lib/query-client";
import { Login } from "@/routes/Login";

// Lazy: the live ops map pulls mapbox-gl — keep it out of the main bundle
// (and off Login) until the route is actually visited.
const LiveMapPage = lazy(() =>
  import("@/features/live-map/LiveMapPage").then((m) => ({ default: m.LiveMapPage })),
);
// Hub detail is map-enabled, so keep Mapbox out of the hubs portfolio route.
const HubDetailPage = lazy(() =>
  import("@/features/hubs/HubDetailPage").then((m) => ({ default: m.HubDetailPage })),
);

export function App() {
  return (
    <ThemeProvider defaultTheme="light" storageKey="bednbody-ui-theme">
      <Toaster richColors position="bottom-right" />
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <BrowserRouter>
            <Routes>
              <Route path="/login" element={<Login />} />

              <Route
                element={
                  <ProtectedRoute>
                    <AppLayout />
                  </ProtectedRoute>
                }
              >
                <Route index element={<OpsDashboardPage />} />

                <Route
                  path="bookings"
                  element={
                    <ProtectedRoute allow={rolesFor("/bookings")}>
                      <BookingsPage />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="attendance"
                  element={
                    <ProtectedRoute allow={rolesFor("/attendance")}>
                      <AttendancePage />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="dispatch"
                  element={
                    <ProtectedRoute allow={rolesFor("/dispatch")}>
                      <DispatchPage />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="calendar"
                  element={
                    <ProtectedRoute allow={rolesFor("/calendar")}>
                      <CalendarRoute />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="live-map"
                  element={
                    <ProtectedRoute allow={rolesFor("/live-map")}>
                      <Suspense fallback={<PageLoading className="min-h-72" />}>
                        <LiveMapPage />
                      </Suspense>
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="support"
                  element={
                    <ProtectedRoute allow={rolesFor("/support")}>
                      <SupportPage />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="mileage"
                  element={
                    <ProtectedRoute allow={rolesFor("/mileage")}>
                      <MileagePage />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="payroll"
                  element={
                    <ProtectedRoute allow={rolesFor("/payroll")}>
                      <PayrollPage />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="rider-sessions"
                  element={
                    <ProtectedRoute allow={rolesFor("/rider-sessions")}>
                      <RiderSessionsPage />
                    </ProtectedRoute>
                  }
                />

                {/* TSESSION-022 names ADMIN. Hub Manager can SEE the queue
                    through 0075 RLS and cannot decide it — CORE-05A §7.4,
                    "Visibility does not create authority." */}
                <Route
                  path="therapist-sessions"
                  element={
                    <ProtectedRoute allow={rolesFor("/therapist-sessions")}>
                      <TherapistSessionsPage />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="roster"
                  element={
                    <ProtectedRoute allow={rolesFor("/roster")}>
                      <RosterPage />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="rankings"
                  element={
                    <ProtectedRoute allow={rolesFor("/rankings")}>
                      <RankingsPage />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="schedules"
                  element={
                    <ProtectedRoute allow={rolesFor("/schedules")}>
                      <SchedulesPage />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="documents"
                  element={
                    <ProtectedRoute allow={rolesFor("/documents")}>
                      <DocumentsPage />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="cash"
                  element={
                    <ProtectedRoute allow={rolesFor("/cash")}>
                      <CashRemittancePage />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="payouts"
                  element={
                    <ProtectedRoute allow={rolesFor("/payouts")}>
                      <PayoutsPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="exec-logs"
                  element={
                    <ProtectedRoute allow={rolesFor("/exec-logs")}>
                      <ExecLogsPage />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="restrictions"
                  element={
                    <ProtectedRoute allow={rolesFor("/restrictions")}>
                      <RestrictionsPage />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="giftpass"
                  element={
                    <ProtectedRoute allow={rolesFor("/giftpass")}>
                      <GiftPassPage />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="giftpass-orders"
                  element={
                    <ProtectedRoute allow={rolesFor("/giftpass-orders")}>
                      <GiftpassOrdersPage />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="analytics"
                  element={
                    <ProtectedRoute allow={rolesFor("/analytics")}>
                      <AnalyticsPage />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="catalog"
                  element={
                    <ProtectedRoute allow={rolesFor("/catalog")}>
                      <CatalogPage />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="hubs"
                  element={
                    <ProtectedRoute allow={rolesFor("/hubs")}>
                      <HubsPage />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="settings"
                  element={
                    <ProtectedRoute allow={rolesFor("/settings")}>
                      <SettingsPage />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="app-status"
                  element={
                    <ProtectedRoute allow={rolesFor("/app-status")}>
                      <AppStatusPage />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="hubs/:hubId"
                  element={
                    <ProtectedRoute allow={rolesFor("/hubs/:hubId")}>
                      <Suspense fallback={<PageLoading className="min-h-72" />}>
                        <HubDetailPage />
                      </Suspense>
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="qualifications"
                  element={
                    // Mirrors grant_service_qualification's own grant (0037).
                    // The RPC refuses a non-superadmin regardless — this is the
                    // UI half of a control enforced in the database.
                    <ProtectedRoute allow={rolesFor("/qualifications")}>
                      <QualificationsPage />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="legal"
                  element={
                    <ProtectedRoute allow={rolesFor("/legal")}>
                      <LegalPage />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="kyc"
                  element={
                    // Superadmin only, mirroring the two admin KYC routes'
                    // own gate. Who may approve a client's identity is an
                    // Open Item (OQ-81) — 0003's table-wide UPDATE on
                    // profiles is a capability, not an approved authority,
                    // and 0070's trigger now blocks that path anyway.
                    <ProtectedRoute allow={rolesFor("/kyc")}>
                      <KycReviewPage />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="audit"
                  element={
                    <ProtectedRoute allow={rolesFor("/audit")}>
                      <AuditPage />
                    </ProtectedRoute>
                  }
                />
              </Route>
            </Routes>
          </BrowserRouter>
        </AuthProvider>
      </QueryClientProvider>
    </ThemeProvider>
  );
}
